import {
    Box,
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableRow,
    Paper,
    TableBody,
    Alert,
    CircularProgress,
    Button,
    Select,
    MenuItem,
    FormControl, TextField,
    Typography
} from '@mui/material';
import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux';
import ClearIcon from '@mui/icons-material/Clear';
import { v4 as uuidv4 } from 'uuid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getEquipmentTransport, postEquipmentTransport } from '../../../../../actions/rof-burns/rof-actions';

export default function RofEquipmentTransport({ equipmentTransport, selectedProject }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [equipmentTransportState, setEquipmentTransportState] = useState([]);
    const dispatch = useDispatch();

    useEffect(() => {
        if (equipmentTransport?.data) {
            setEquipmentTransportState(
                equipmentTransport.data.map((d) => ({
                    ...d,
                    disabled: true
                }))
            );
        }
    }, [equipmentTransport]);

    const handleAddEntry = () => {
        const newEntry = {
            rowId: uuidv4(),
            date: '',
            equipment_transported: '',
            equipment_mass: '',
            distance_transported: '',
            fuel_type: '',
            transport_type: '',
            disabled: false
        };
        setEquipmentTransportState(prev => [...prev, newEntry]);
    }

    useEffect(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, [equipmentTransportState]);

    const handleDateSelection = (date, rowId) => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        handleInputChange(rowId, 'date', formattedDate);
    };

    const handleInputChange = (rowId, field, value) => {
        setEquipmentTransportState(prev =>
            prev.map(row =>
                row.rowId === rowId ? { ...row, [field]: value } : row
            )
        );
    };

    const handleClearEntry = (rowIdToRemove) => {
        setEquipmentTransportState((prevState) =>
            prevState.filter((item) => item.rowId !== rowIdToRemove)
        );
    }

    // Helper: check if row is empty (all fields blank)
    const isEmptyRow = (row) => {
        return (
            !row.date &&
            !row.equipment_transported &&
            !row.equipment_mass &&
            !row.distance_transported &&
            !row.fuel_type &&
            !row.transport_type
        );
    };

    // Helper: check if row is complete (all required fields filled)
    const isCompleteRow = (row) => {
        return (
            !!row.date &&
            !!row.equipment_transported &&
            !!row.equipment_mass &&
            !!row.distance_transported &&
            !!row.fuel_type &&
            !!row.transport_type
        );
    };

    // Helper: validate a complete row, return error message or null
    const validateCompleteRow = (row) => {
        if (!row.date) return "Date is required.";
        if (!row.equipment_transported) return "Equipment transported is required.";
        if (!row.equipment_mass) return "Equipment mass is required.";
        if (!row.distance_transported) return "Distance transported is required.";
        if (!row.fuel_type) return "Fuel type is required.";
        if (!row.transport_type) return "Transport type is required.";
        // Add more validation as needed
        return null;
    };



    const handleSubmitData = async () => {
        setLoading(true);
        setError(false);
        setErrorMsg && setErrorMsg("");

        try {
            const emptyRows = [];
            const partialRows = [];
            const completeRows = [];

            // Only consider rows with rowId (i.e., new/unsaved)
            const rowsToSubmit = equipmentTransportState.filter(row => row.rowId && !row.id);

            rowsToSubmit.forEach((row, idx) => {
                if (isEmptyRow(row)) {
                    emptyRows.push(row);
                } else if (isCompleteRow(row)) {
                    completeRows.push({ index: idx, row });
                } else {
                    partialRows.push({ index: idx, row });
                }
            });

            if (partialRows.length > 0) {
                const first = partialRows[0];
                setError(true);
                setErrorMsg &&
                    setErrorMsg(
                        `Row ${first.index + 1} is partially completed. Please fill all required fields or clear the row.`
                    );
                setLoading(false);
                return;
            }

            for (const { index, row } of completeRows) {
                const msg = validateCompleteRow(row);
                if (msg) {
                    setError(true);
                    setErrorMsg && setErrorMsg(`Row ${index + 1}: ${msg}`);
                    setLoading(false);
                    return;
                }
            }

            if (completeRows.length === 0) {
                setLoading(false);
                return;
            }

            const payload = [];
            completeRows.forEach(({ row }) => {
                const entry = {
                    date: row.date,
                    equipmentTransported: row.equipment_transported,
                    equipmentMass: String(row.equipment_mass),
                    distanceTransported: String(row.distance_transported),
                    fuelType: row.fuel_type,
                    transportType: row.transport_type
                };
                payload.push(entry);
            });

            const formData = new FormData();
            formData.append("data", JSON.stringify(payload));

            dispatch(postEquipmentTransport(formData, selectedProject.id)).then(() => {
                dispatch(getEquipmentTransport(selectedProject.id));
            });
        } catch (e) {
            setError(true);
            setErrorMsg && setErrorMsg("Failed to submit data. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    const fuelTypes = [
        { id: 0, name: 'diesel' },
        { id: 1, name: 'petrol' }
    ]
    const transportTypes = [{ id: 0, name: 'Medium-And-Heavy-Duty-Truck' }]

    return (
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant='h5'>Equipment Transport</Typography>
            <TableContainer component={Paper} sx={{ height: 'auto' }}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">
                                Date
                            </TableCell>
                            <TableCell align="left">
                                List Equipment/Asset Transported
                            </TableCell>
                            <TableCell align="left">
                                Mass of Equipment (metric tonne)
                            </TableCell>
                            <TableCell align="left">
                                Distance Transported (km)
                            </TableCell>
                            <TableCell align="left">
                                Fuel Type
                            </TableCell>
                            <TableCell align="left">
                                Transport Type
                            </TableCell>
                            <TableCell align="left">
                                Remove Row
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {equipmentTransportState?.map((data, i) => {
                            return (
                                <TableRow key={i}>
                                    <TableCell align="left">
                                        <FormControl>
                                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                                <DatePicker
                                                    name="date"
                                                    value={data.date ? moment(data.date, 'YYYY-MM-DD') : null}
                                                    onChange={date => handleDateSelection(date, data.rowId)}
                                                    disabled={data.disabled ?? false}
                                                    format="YYYY-MM-DD"
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Select
                                            name="equipment_transported"
                                            value={data.equipment_transported}
                                            disabled={data.disabled ?? false}
                                            fullWidth
                                            onChange={e => handleInputChange(data.rowId, 'equipment_transported', e.target.value)}
                                        >
                                            {data.disabled ? <MenuItem key={data.equipment_transported} value={data.equipment_transported}>{data.equipment_transported}</MenuItem> : equipmentTransport?.equipment?.map(e => (
                                                <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell align="left">
                                        <TextField
                                            name="equipment_mass"
                                            value={data.equipment_mass}
                                            onChange={e => handleInputChange(data.rowId, 'equipment_mass', e.target.value)}
                                            type="number"
                                            disabled={data.disabled ?? false}
                                        />
                                    </TableCell>
                                    <TableCell align="left">
                                        <TextField
                                            name="distance_transported"
                                            value={data.distance_transported}
                                            onChange={e => handleInputChange(data.rowId, 'distance_transported', e.target.value)}
                                            type="number"
                                            disabled={data.disabled ?? false}
                                        />
                                    </TableCell>
                                    <TableCell align="left">
                                        <Select
                                            name="fuel_type"
                                            value={data.fuel_type}
                                            disabled={data.disabled ?? false}
                                            fullWidth
                                            onChange={e => handleInputChange(data.rowId, 'fuel_type', e.target.value)}
                                        >
                                            {fuelTypes.map(type => (
                                                <MenuItem key={type.id} value={type.name}>{type.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Select
                                            name="fuel_type"
                                            value={data.transport_type}
                                            disabled={data.disabled ?? false}
                                            fullWidth
                                            onChange={e => handleInputChange(data.rowId, 'transport_type', e.target.value)}
                                        >
                                            {transportTypes.map(type => (
                                                <MenuItem key={type.id} value={type.name}>{type.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell align="left">
                                        {data.rowId && i === equipmentTransportState.length - 1 ? (
                                            <Box onClick={() => handleClearEntry(data.rowId)}
                                                sx={{ cursor: 'pointer', color: 'red', mt: 1 }}>
                                                <ClearIcon />
                                            </Box>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {error ? <Alert severity='error' sx={{ mb: 2 }}>{errorMsg}</Alert> : null}
            <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                {loading ?
                    <CircularProgress
                        sx={{ ml: 6 }}>
                    </CircularProgress> :
                    <Button variant='contained' onClick={handleAddEntry}>Add Entry</Button>
                }
                {!loading && <Button
                    variant='contained'
                    sx={{ height: 'auto' }}
                    onClick={handleSubmitData}>
                    Submit Data
                </Button>}
            </Box>
        </Grid>
    )
}
